import React from 'react';
import { Button } from '@/components/ui/button';
import { PlanType } from '@/config/pricing';
import MixpanelService from '@/services/mixpanel';
import { useIsMobile } from '@/hooks/use-mobile';

type PricingCardProps = {
  id: PlanType;
  name: string;
  price: number;
  originalPrice: number;
  features: string[];
  credits: number;
  recommended?: boolean;
  onSelect?: (planId: PlanType) => void;
  learnMoreLink?: {
    text: string;
    sectionId: string;
    carouselIndex: number;
  };
  position?: 'first' | 'middle' | 'last';
  isContactUs?: boolean;
  contactUrl?: string;
  hidePrice?: boolean;
};

const PricingCard: React.FC<PricingCardProps> = ({
  id,
  name,
  price,
  originalPrice,
  features,
  recommended = false,
  credits,
  onSelect,
  learnMoreLink,
  position = 'middle',
  isContactUs = false,
  contactUrl,
  hidePrice = false,
}) => {
  const isMobile = useIsMobile();
  const savingsPercentage = Math.round(((originalPrice - price) / originalPrice) * 100);

  // Helper function to format numbers with commas
  const formatPrice = (amount: number) => {
    return amount.toLocaleString();
  };

  const handleButtonClick = () => {
    if (isContactUs && contactUrl) {
      // Track the contact us click
      MixpanelService.trackButtonClick("Contact Us", {
        plan_id: id,
        plan_name: name,
        contact_url: contactUrl,
      });

      // Open the calendly URL
      window.open(contactUrl, '_blank');
      return;
    }

    // Track the plan selection
    MixpanelService.trackButtonClick("Select Plan", {
      plan_id: id,
      plan_name: name,
      plan_price: price,
      plan_credits: credits,
      recommended: recommended,
    });

    if (onSelect) {
      onSelect(id);
    }

    // Scroll to the CTA section
    const ctaSection = document.getElementById('cta-section');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnMoreClick = () => {
    if (!learnMoreLink) return;

    // Track the learn more click
    MixpanelService.trackButtonClick("Learn More", {
      plan_id: id,
      plan_name: name,
      learn_more_section: learnMoreLink.sectionId,
      carousel_index: learnMoreLink.carouselIndex
    });

    // Scroll to the target section
    const targetSection = document.getElementById(learnMoreLink.sectionId);
    if (targetSection) {
      targetSection.scrollIntoView({ behavior: 'smooth' });

      // Dispatch a custom event to tell the carousel to go to specific index
      const event = new CustomEvent('select-carousel-item', {
        detail: { index: learnMoreLink.carouselIndex }
      });
      document.dispatchEvent(event);
    }
  };

  // Determine border radius based on position and screen size
  const getBorderRadius = () => {
    if (isMobile) {
      // On mobile, round top corners for first card and bottom corners for last card
      switch (position) {
        case 'first':
          return 'rounded-t-2xl';
        case 'last':
          return 'rounded-b-2xl';
        default:
          return '';
      }
    } else {
      // On desktop, round left corners for first card and right corners for last card
      switch (position) {
        case 'first':
          return 'rounded-l-2xl';
        case 'last':
          return 'rounded-r-2xl';
        default:
          return '';
      }
    }
  };

  // Determine border styling
  const getBorderStyle = () => {
    // Base styles for top and bottom borders
    const baseBorder = recommended
      ? 'border-t-2 border-b-2 border-primary'
      : 'border-t border-b';

    // Add left border for first position
    if (position === 'first') {
      return `${baseBorder} border-l-2 ${recommended ? 'border-l-primary' : 'border-l'}`;
    }
    // Add right border for last position
    else if (position === 'last') {
      return `${baseBorder} border-r-2 ${recommended ? 'border-r-primary' : 'border-r'}`;
    }
    // Add right border for middle positions
    else {
      return `${baseBorder} border-r border-r-gray-200`;
    }
  };

  return (
    <div
      className={`w-full bg-white ${getBorderStyle()} ${getBorderRadius()} p-6
      ${recommended ? 'z-10 shadow-md transform lg:-translate-y-4' : ''}
      animate-on-scroll flex flex-col h-full relative
      ${position === 'first' ? '' : 'border-l-0'}`}
      style={{ flex: 1 }}
    >
      {recommended && (
        <div className="absolute z-10 -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-xs px-3 py-1 rounded-full uppercase font-semibold">
          Recommended
        </div>
      )}
      <div className="flex flex-col h-full">
        <div className="flex-none">
          <h3 className="text-xl font-bold mb-2">{name}</h3>
          {!hidePrice && (
            <div className="mb-4">
              <p className="text-3xl font-semibold">${formatPrice(price)}</p>
            </div>
          )}
        </div>
        {(!hidePrice) ? (
          <p className="text-sm text-gray-700 mb-6">
            Includes {formatPrice(credits)} credits
          </p>
        ) : (
          <p className="text-sm text-gray-700 mb-6">
            Custom pricing
          </p>
        )}
        <ul className="flex-grow text-sm text-gray-700 space-y-3 mb-6">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <span className="text-secondary mr-2">✓</span> {feature}
            </li>
          ))}
        </ul>
        <div className="flex-none pt-4 flex flex-col items-center">
          <Button
            variant={recommended ? "default" : "outline"}
            className={`w-full ${recommended ? '' : 'border-primary text-primary hover:bg-primary hover:text-white'}`}
            onClick={handleButtonClick}
            data-plan-id={id} // Add data attribute for debugging
          >
            {isContactUs ? "Contact us" : "Buy now"}
          </Button>

          {learnMoreLink && (
            <button
              onClick={handleLearnMoreClick}
              className="mt-2 text-sm text-primary hover:text-primary-700 hover:underline h-10"
            >
              {learnMoreLink.text}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingCard;
